import { useQuery } from 'data-fetching-client';
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { GET_PURCHASE_OPTIONS } from 'pricing-pages-api/purchase-options/queries';
import { useAppSelector } from '../reducers';
import { hasPricingAndPurchasingSucceededSelector } from '../selectors/LoadingStateSelectors';
import { planBuilderCheckoutProductConfigurationsSelector, planBuilderCommonTermForProductConfigurationsSelector } from '../selectors/PlanBuilderSelectors';
import { convertSkuIdsToSkuUuidsForProductConfigurationsSelector } from '../selectors/ProductConfigsSelectors';
import { skuIdIsQuantityPackSelector } from '../selectors/ProductsSelectors';
import { useQueryVariables } from './useBulkPurchaseOptions';
export const usePurchaseOptions = ({
  products = [],
  term,
  skip,
  onCompleted,
  onError
}) => {
  const defaultQueryVariables = useQueryVariables();
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  return useQuery(GET_PURCHASE_OPTIONS, {
    variables: Object.assign({
      products,
      term
    }, defaultQueryVariables),
    onCompleted,
    onError,
    skip: skip || !((products === null || products === void 0 ? void 0 : products.length) > 0) || !hasPricingAndPurchasingSucceeded || !defaultQueryVariables.currencyCode || !defaultQueryVariables.language
  });
};
export const usePurchaseOptionsForProducts = ({
  productConfigurations,
  term,
  skip,
  onCompleted,
  onError
}) => {
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const products = useAppSelector(state => {
    if (hasPricingAndPurchasingSucceeded) {
      return convertSkuIdsToSkuUuidsForProductConfigurationsSelector(state, {
        productConfigurations
      });
    }
  });
  const {
    data,
    loading,
    error
  } = usePurchaseOptions({
    products,
    term,
    skip,
    onCompleted,
    onError
  });
  return {
    loading,
    error,
    purchaseOptions: data === null || data === void 0 ? void 0 : data.purchaseOptions
  };
};
export const usePurchaseOptionsForProductsForTerm = ({
  productConfigurations,
  term = 'annual',
  skip,
  onCompleted,
  onError
}) => {
  // using a ref so the callback doesn't have stale data
  const termRef = useRef(term);
  termRef.current = term;
  const {
    purchaseOptions,
    loading,
    error
  } = usePurchaseOptionsForProducts({
    productConfigurations,
    term,
    skip,
    onCompleted: data => {
      var _data$purchaseOptions, _data$purchaseOptions2;
      const purchaseOptionToReturn = (data === null || data === void 0 || (_data$purchaseOptions = data.purchaseOptions) === null || _data$purchaseOptions === void 0 ? void 0 : _data$purchaseOptions.find(current => current.offerInfo.payment === termRef.current)) || (data === null || data === void 0 || (_data$purchaseOptions2 = data.purchaseOptions) === null || _data$purchaseOptions2 === void 0 ? void 0 : _data$purchaseOptions2[0]);
      if (onCompleted) {
        onCompleted({
          pricingData: purchaseOptionToReturn
        });
      }
    },
    onError
  });
  const purchaseOptionToReturn = useMemo(() => {
    return (purchaseOptions === null || purchaseOptions === void 0 ? void 0 : purchaseOptions.find(current => {
      return current.offerInfo.payment === term;
    })) || (purchaseOptions === null || purchaseOptions === void 0 ? void 0 : purchaseOptions[0]);
  }, [purchaseOptions, term]);
  return {
    loading,
    error,
    pricingData: purchaseOptionToReturn
  };
};
export const usePlanBuilderPurchaseOptions = ({
  term,
  onCompleted,
  onError
}) => {
  const checkoutProductConfigurations = useAppSelector(planBuilderCheckoutProductConfigurationsSelector);
  const commonTermForProductConfigurations = useAppSelector(planBuilderCommonTermForProductConfigurationsSelector);
  const productConfigurationsWithoutQuantityPacks = useAppSelector(state => checkoutProductConfigurations.filter(({
    skuId
  }) => !skuIdIsQuantityPackSelector(state, {
    skuId
  })));
  const {
    pricingData,
    loading,
    error
  } = usePurchaseOptionsForProductsForTerm({
    productConfigurations: productConfigurationsWithoutQuantityPacks,
    term: term || commonTermForProductConfigurations,
    onCompleted,
    onError
  });
  return {
    loading,
    error,
    pricingData
  };
};